import { useConfigStore } from '@/store/config.store';
import { useEditor2D } from '@/composables/useEditor2D';
import { useEditor3D } from '@/composables/useEditor3D';
import { hasZoom3dRoomIn, hasZoom3dRoomOut, zoom3dFactorIn, zoom3dFactorOut } from '@/utils/zoom3dFactor';
import { ZOOM_MAX_RATIO, ZOOM_MIN_RATIO, ZOOM_STEP_RATIO } from '@/config';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

// Floating-point slack when comparing the readout against a bound. The clamp
// lands exactly on the limit, but the ratio is a division, so an exact
// comparison can miss by an ulp and leave a button live at the limit.
const ZOOM_RATIO_EPSILON = 0.001;

function useZoomControl() {
    const { currentSide, fZoom, fZoomDefault, zoom3dFactor } = storeToRefs(useConfigStore());

    // 3D: the camera frames the product at a distance factor, stepped and
    // clamped like the 2D zoom. zoom3dFactor is the intent, and TheProduct3DView
    // resyncs it from the camera on any move we did not cause — so the wheel,
    // which dollies through OrbitControls without passing here, can no longer
    // leave it stale and make the buttons step from fiction.
    function _apply3DZoom(factor: number) {
        zoom3dFactor.value = factor;
        useEditor3D()?.controller.camera.zoomToLevel(factor);
    }

    function zoom() {
        _apply3DZoom(zoom3dFactorIn(zoom3dFactor.value));
    }

    function unZoom() {
        _apply3DZoom(zoom3dFactorOut(zoom3dFactor.value));
    }

    const canZoom3dIn = computed(() => hasZoom3dRoomIn(zoom3dFactor.value));
    const canZoom3dOut = computed(() => hasZoom3dRoomOut(zoom3dFactor.value));

    function zoomHome() {
        useEditor2D(currentSide.value)?.viewport.zoomToFit();
    }

    // No clamping here — zoomTo owns the bounds, so the buttons, the wheel and
    // the pinch cannot drift apart. Clamping locally is what made zoom out
    // raise the zoom: the floor sat above the default framing.
    function zoomIn() {
        const editor = useEditor2D(currentSide.value);
        if (!editor) {
            return;
        }
        editor.viewport.zoomTo(editor.viewport.getZoom() * ZOOM_STEP_RATIO);
    }

    function zoomOut() {
        const editor = useEditor2D(currentSide.value);
        if (!editor) {
            return;
        }
        editor.viewport.zoomTo(editor.viewport.getZoom() / ZOOM_STEP_RATIO);
    }

    // Both refs, unconditionally: an early return that skipped reading fZoom
    // left the computed cached with no dependency on it.
    const zoomRatio = computed(() => fZoom.value / (fZoomDefault.value || 1));
    const canZoomIn = computed(() => zoomRatio.value < ZOOM_MAX_RATIO - ZOOM_RATIO_EPSILON);
    const canZoomOut = computed(() => zoomRatio.value > ZOOM_MIN_RATIO + ZOOM_RATIO_EPSILON);

    return {
        canZoom3dIn,
        canZoom3dOut,
        canZoomIn,
        canZoomOut,
        unZoom,
        zoom,
        zoomHome,
        zoomIn,
        zoomOut,
        zoomRatio,
    };
}

export { useZoomControl };
